import React, { useState, useEffect, useContext } from 'react';
import { collection, query, where, onSnapshot, orderBy, doc, getDoc, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import AuthContext, { UserData } from '../contexts/AuthContext';
import MessageThread from '../components/messages/MessageThread';
import ConversationList from '../components/messages/ConversationList';
import { ArrowLeftIcon, ChatBubbleLeftRightIcon, PaperAirplaneIcon } from '@heroicons/react/24/outline';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface Conversation {
  id: string;
  participants: string[];
  lastMessage: string;
  lastMessageTime: any;
  unreadCount?: number;
  otherUser?: UserData;
}

const Messages: React.FC = () => {
  const { currentUser } = useContext(AuthContext);
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [selectedConversation, setSelectedConversation] = useState<Conversation | null>(null);
  const [loading, setLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);
  const [showConversationList, setShowConversationList] = useState(true);

  const [searchParams] = useSearchParams();
  const userIdFromUrl = searchParams.get('user');
  const { t } = useTranslation();

  // Manejar cambio de tamaño de pantalla
  useEffect(() => {
    const handleResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobileView(mobile);
      if (!mobile) {
        setShowConversationList(true);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const startNewConversation = async (userId: string) => {
    if (!currentUser) return;

    // Verificar si ya existe una conversación con este usuario
    const existingConversation = conversations.find(
      (conv) => conv.participants.includes(userId)
    );

    if (existingConversation) {
      setSelectedConversation(existingConversation);
      if (isMobileView) {
        setShowConversationList(false);
      }
      return;
    }

    // Obtener datos del usuario
    const userDoc = await getDoc(doc(db, 'users', userId));
    if (!userDoc.exists()) return;

    const otherUserData = {
      ...userDoc.data() as Omit<UserData, 'uid'>,
      uid: userDoc.id
    };

    // Crear nueva conversación
    const newConversationRef = await addDoc(collection(db, 'conversations'), {
      participants: [currentUser.uid, userId],
      lastMessage: '',
      lastMessageTime: serverTimestamp(),
      createdAt: serverTimestamp()
    });

    const newConversation: Conversation = {
      id: newConversationRef.id,
      participants: [currentUser.uid, userId],
      lastMessage: '',
      lastMessageTime: new Date(),
      otherUser: otherUserData
    };

    setConversations([newConversation, ...conversations]);
    setSelectedConversation(newConversation);
    if (isMobileView) {
      setShowConversationList(false);
    }
  };

  useEffect(() => {
    if (!currentUser?.uid) return;

    const conversationsQuery = query(
      collection(db, 'conversations'),
      where('participants', 'array-contains', currentUser.uid),
      orderBy('lastMessageTime', 'desc')
    );

    const unsubscribe = onSnapshot(conversationsQuery, async (snapshot) => {
      const conversationsData: Conversation[] = [];

      for (const docSnapshot of snapshot.docs) {
        const conversationData = docSnapshot.data() as Omit<Conversation, 'id'>;

        // Encontrar el otro usuario en la conversación
        const otherUserId = conversationData.participants.find(
          (id: string) => id !== currentUser.uid
        );

        // Obtener información del otro usuario
        if (otherUserId) {
          const userQuery = query(
            collection(db, 'users'),
            where('uid', '==', otherUserId)
          );

          const userSnapshot = await new Promise<any>((resolve) => {
            const unsubUser = onSnapshot(userQuery, (userSnap) => {
              resolve(userSnap);
              unsubUser();
            });
          });

          if (!userSnapshot.empty) {
            const otherUserData = userSnapshot.docs[0].data();

            conversationsData.push({
              id: docSnapshot.id,
              ...conversationData,
              otherUser: otherUserData
            });
          }
        }
      }

      setConversations(conversationsData);
      setLoading(false);

      // Si hay un userId en la URL, iniciar conversación con ese usuario
      if (userIdFromUrl && currentUser) {
        // Verificar si ya existe una conversación con este usuario
        const existingConversation = conversationsData.find(
          (conv) => conv.otherUser?.uid === userIdFromUrl
        );

        if (existingConversation) {
          // Si existe, seleccionarla
          setSelectedConversation(existingConversation);
          if (isMobileView) {
            setShowConversationList(false);
          }
        } else {
          // Si no existe, crear una nueva conversación
          startNewConversation(userIdFromUrl);
        }
      }
    });

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser, userIdFromUrl, isMobileView]);

  const handleSelectConversation = (conversation: Conversation) => {
    setSelectedConversation(conversation);
    if (isMobileView) {
      setShowConversationList(false);
    }
  };



  if (!currentUser) {
    return (
      <div className="flex justify-center items-center h-[calc(100vh-64px)]">
        <p className="text-gray-600 dark:text-gray-400">{t('messages.loginRequired')}</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-[calc(100vh-64px)] w-full overflow-hidden bg-white dark:bg-gray-900 absolute inset-x-0 top-[64px]">
      {/* Encabezado principal - Estilo Instagram */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 z-10 flex-shrink-0">
        <h1 className="text-xl font-semibold">{t('common.messages')}</h1>
        <div className="flex items-center space-x-2">
          <button className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700">
            <PaperAirplaneIcon className="h-5 w-5 rotate-45" />
          </button>
        </div>
      </div>

      {/* Contenedor principal con layout flexible - Ocupa todo el espacio disponible */}
      <div className="flex flex-1 overflow-hidden">
        {/* Panel izquierdo - Lista de conversaciones */}
        <div
          className={`${isMobileView ? (showConversationList ? 'w-full' : 'hidden') : 'w-[350px]'} 
                      flex-shrink-0 border-r border-gray-200 dark:border-gray-800 flex flex-col h-full overflow-hidden`}
        >
          <ConversationList
            conversations={conversations}
            selectedConversation={selectedConversation}
            onSelectConversation={handleSelectConversation}
            loading={loading}
          />
        </div>

        {/* Panel derecho - Área de mensajes */}
        <div
          className={`${isMobileView ? (!showConversationList ? 'w-full' : 'hidden') : 'flex-grow'} 
                      flex flex-col h-full overflow-hidden`}
        >
          {selectedConversation ? (
            <div className="flex flex-col h-full overflow-hidden">
              {/* Encabezado de conversación */}
              <div className="flex items-center px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-800 sticky top-0 z-10 flex-shrink-0">
                {isMobileView && (
                  <button
                    onClick={() => setShowConversationList(true)}
                    className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 mr-2"
                  >
                    <ArrowLeftIcon className="h-5 w-5" />
                  </button>
                )}
                <div className="flex items-center flex-grow">
                  <img
                    src={selectedConversation.otherUser?.photoURL || '/default-avatar.png'}
                    alt=""
                    className="h-8 w-8 rounded-full mr-3 object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.onerror = null;
                      target.src = '/default-avatar.png';
                    }}
                  />
                  <div>
                    <span className="font-medium">{selectedConversation.otherUser?.displayName}</span>
                    {/* Indicador de actividad - se puede implementar más adelante */}
                    {/* <p className="text-xs text-gray-500">Activo hace 10 min</p> */}
                  </div>
                </div>
              </div>

              {/* Área de chat */}
              <div className="flex-grow overflow-hidden h-full">
                <MessageThread
                  conversation={selectedConversation}
                  currentUser={currentUser}
                />
              </div>
            </div>
          ) : (
            <div className="flex flex-col justify-center items-center h-full p-4 text-center">
              <div className="max-w-md">
                <ChatBubbleLeftRightIcon className="h-16 w-16 text-gray-300 mx-auto mb-6" />
                <h3 className="text-xl font-semibold mb-3">{t('messages.directMessages')}</h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {t('messages.selectConversation')}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>


    </div>
  );
};

export default Messages;
